<template>
  <div>
    <b-list-group :key="componentKey" v-visible="visibleChanged">
      <b-list-group-item v-for="(threshold, index) in thresholds" :key="index">
        <dx-range-selector  ref="dxrange"
                            :value.sync="threshold.value"
                            :selectedRangeColor="threshold.color"
                            @valueChanged="thresholdValueChanged(threshold, $event)">
          <dx-behavior />
          <dx-size :height="50" />
          <dx-scale :startValue="0" :endValue="100" :minorTickInterval="0.50" :minRange="0.50">
            <dx-scale-label :visible="false" />
          </dx-scale>
          <dx-slider-marker format="##0.00'%'" />
        </dx-range-selector>

        <b-form-group class="list-inline-item" :description="$t('Webportal.Minimum')">
          <dx-number-box  v-model="threshold.value.startValue"
                          @valueChanged="thresholdStartValueChanged(threshold, $event)"
                          format="##0.00'%'" width="6em" stylingMode="underlined"
                          :show-spin-buttons="true" :step="0.5" :min="0" :max="threshold.value.endValue - 0.01"
                          :disabled="index == 0" />
        </b-form-group>
        <b-form-group class="list-inline-item" :description="$t('Webportal.Maximum')">
          <dx-number-box v-model="threshold.value.endValue"
                        @valueChanged="thresholdEndValueChanged(threshold, $event)"
                        format="##0.00'%'" width="6em" stylingMode="underlined"
                        :show-spin-buttons="true" :step="0.5" :min="threshold.value.startValue + 0.01" :max="100"
                        :disabled="index == thresholds.length - 1" />
        </b-form-group>
        <b-form-group class="list-inline-item" :description="$t('Webportal.Color')">
          <dx-color-box v-model="threshold.color" @valueChanged="onInput(thresholds)" width="10em" stylingMode="underlined" />
        </b-form-group>
        <b-form-group class="list-inline-item">
          <template #description>
            {{ $t('Webportal.Icons') }}
            <b-link class="text-danger" @click="deleteAllThresholdIcons(threshold)">
              <font-awesome-icon icon="trash-alt" /> {{ $t('Webportal.DeleteAll') }}
            </b-link>
          </template>
          <b-dropdown v-for="icon in threshold.icons" :key="icon.Position" variant="outline-secondary" class="mr-1 mb-1" size="sm">
            <template v-slot:button-content>
              <font-awesome-icon fixed-width :color="threshold.color" :icon="[icon.Style, icon.Name]" />
            </template>
            <b-dropdown-item @click="deleteThresholdIcon(threshold, icon)">
              <span class="text-danger">
                <font-awesome-icon fixed-width icon="trash-alt" /> {{ $t('Webportal.Delete') }}
              </span>
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item v-for="(availableIcon, availableIconIndex) in availableIcons" :key="availableIconIndex" @click="changeThresholdIcon(threshold, icon, availableIcon)">
              <font-awesome-icon fixed-width :icon="[availableIcon.Style, availableIcon.Name]" /> {{ availableIcon.Name }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown v-if="threshold.icons?.length < 10" variant="outline-secondary" class="mr-1 mb-1" size="sm">
            <template v-slot:button-content>
              &nbsp;&nbsp;&nbsp;
            </template>
            <b-dropdown-item v-for="(availableIcon, availableIconIndex) in availableIcons" :key="availableIconIndex" @click="addThresholdIcon(threshold, availableIcon)">
              <font-awesome-icon fixed-width :icon="[availableIcon.Style, availableIcon.Name]" />
            </b-dropdown-item>
          </b-dropdown>
        </b-form-group>

        <b-button variant="outline-danger" size="sm" class="float-end align-bottom mt-4"
                  v-if="index > 0"
                  @click="removeThreshold(threshold)">
          <font-awesome-icon icon="trash-alt" /> {{ $t('Webportal.Delete') }}
        </b-button>
      </b-list-group-item>
    </b-list-group>

    <b-button class="mt-3" variant="secondary" size="sm"
              @click="addThreshold()">
      <font-awesome-icon icon="plus" /> {{ $t('Webportal.Administration.AddThreshold') }}
    </b-button>
    <b-button class="mt-3" variant="secondary" size="sm"
              @click="resetToDefaults()" :disabled="thresholds == null">
      <font-awesome-icon icon="sliders-h-square" /> {{ $t('Webportal.Administration.ResetToDefault') }}
    </b-button>
  </div>
</template>

<script>

import { DxRangeSelector, DxNumberBox, DxColorBox } from 'devextreme-vue';
import { DxBehavior, DxSize, DxScale, DxScaleLabel, DxSliderMarker, DxMinorTick } from 'devextreme-vue/range-selector';

export default {
  name: "ThresholdSelector",
  directives: {
    visible: {
      mounted(el, binding) {
        const observer = new IntersectionObserver(
          ([e]) => binding.value(e.isIntersecting, e),
          { threshold: 0.1 }
        );
        el._observer = observer;
        observer.observe(el);
      },
      unmounted(el) {
        el._observer?.disconnect();
      },
    },
  },
  props: {
    modelValue: {
      type: Array,
      default: null
    }
  },

  components : {
    DxRangeSelector,
    DxNumberBox,
    DxBehavior,
    DxSize,
    DxScale,
    DxScaleLabel,
    DxSliderMarker,
    DxColorBox
  },

  data() {
    return {
      thresholds: null,  // Initial null, wird im mounted gesetzt
      componentKey: 0,
      nextThresholdId: 0,
      availableIcons: [
        { Name: 'frown-open', Style: 'far' },
        { Name: 'meh', Style: 'far' },
        { Name: 'grin', Style: 'far' },
        { Name: 'star', Style: 'far' },
        { Name: 'star', Style: 'fas' },
        { Name: 'star-half', Style: 'fas' },
        { Name: 'star-half-alt', Style: 'fas' },
      ],
    }
  },

  mounted() {
    this.thresholds = this.modelValue;
    this.ensureIds(this.thresholds);
  },

  watch: {
    modelValue (newVal, oldVal) {
      this.thresholds = newVal;
      this.ensureIds(this.thresholds);
      this.componentKey += 1;
    },
    thresholds (newVal, oldVal) {
      this.onInput(newVal);
    }
  },

  methods: {
    ensureIds(list) {
      if (!Array.isArray(list)) return;
      for (const t of list) {
        if (!t._id) t._id = this.nextThresholdId++;
        t.icons ??= [];
      }
    },

    visibleChanged(isVisible) {
      if (isVisible && this.$refs.dxrange)
        for (const dxrange of this.$refs.dxrange)
          dxrange.instance.render();
    },

    onInput(value) {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      this.$emit('change');
    },

    addThreshold() {
      if (!this.thresholds)
        this.thresholds = [];

      let threshold = {
        _id: this.nextThresholdId++,
        value: {
          startValue: this.thresholds.length === 0 ? 0 : 95,
          endValue: 100
        }, icons: [], color: "#000000" };

      this.thresholds.push(threshold);
      this.thresholdValueChanged(threshold);
    },

    removeThreshold(threshold) {
      this.thresholds.splice(this.thresholds.findIndex(x => x._id == threshold._id), 1);
      this.thresholdValueChanged(this.thresholds[this.thresholds.length - 1]);
    },

    thresholdStartValueChanged(threshold, event) {
      threshold.value = { startValue: event.value, endValue: threshold.value.endValue };
      this.thresholdValueChanged(threshold);
    },

    thresholdEndValueChanged(threshold, event) {
      threshold.value = { startValue: threshold.value.startValue, endValue: event.value };
      this.thresholdValueChanged(threshold);
    },

    thresholdValueChanged(threshold, event) {
      if (event && event.value) {
        threshold.value = { startValue: event.value[0], endValue: event.value[1] };
      }
      this.checkThresholds(threshold);
      this.onInput(this.thresholds);
    },

    addThresholdIcon(threshold, icon) {
      threshold.icons.push(Object.assign({ Position: threshold.icons.length + 1 }, icon));
      this.onInput(this.thresholds);
    },

    changeThresholdIcon(threshold, icon, selectedIcon) {
      Object.assign(icon, selectedIcon);
      this.onInput(this.thresholds);
    },

    deleteThresholdIcon(threshold, icon) {
      threshold.icons.splice(threshold.icons.findIndex((element) => element === icon), 1);
      threshold.icons.forEach(element => {
        if (element.Position > icon.Position)
          element.Position--;
      });
      this.onInput(this.thresholds);
    },

    deleteAllThresholdIcons(threshold) {
      threshold.icons = [];
      this.onInput(this.thresholds);
    },

    resetToDefaults() {
      this.nextThresholdId = 0;
      this.thresholds = [
        { _id: this.nextThresholdId++, value: { startValue: 0, endValue: 70 - 0.01 }, color: "#b71402", icons: [{ Name: "frown-open", Style: "far", Position: 1 }] },
        { _id: this.nextThresholdId++, value: { startValue: 70, endValue: 90 - 0.01 }, color: "#ffd900", icons: [{ Name: "meh", Style: "far", Position: 1 }] },
        { _id: this.nextThresholdId++, value: { startValue: 90, endValue: 100 }, color: "#36c259", icons: [{ Name: "grin", Style: "far", Position: 1 }] }
      ];
    },

    checkThresholds(mainThreshold) {
      if (!mainThreshold._id && mainThreshold._id !== 0) {
        console.error("Main threshold has no _id!", mainThreshold);
      }

      let indexMain = this.thresholds.findIndex(x => x._id == mainThreshold._id);
      if (indexMain < 0) {
        // deleting threshold
        if (mainThreshold._id >= 0) return;

        console.error("Could not find main threshold in thresholds list!", mainThreshold, this.thresholds);
      }

      // Check lower bound of 0 for first threshold
      if (indexMain == 0 && mainThreshold.value.startValue > 0)
        mainThreshold.value = { startValue: 0, endValue: mainThreshold.value.endValue };

      // Check upper bound of 100 for last threshold
      if (indexMain == this.thresholds.length - 1 && mainThreshold.value.endValue < 100)
        mainThreshold.value = { startValue: mainThreshold.value.startValue, endValue: 100 };

      // Adapt boundaries of all following thresholds
      let lastMaxValue = Math.min(mainThreshold.value.endValue + 0.01, 100);
      for (let i = indexMain + 1; i < this.thresholds.length; i++)
      {
        let t = this.thresholds[i];
        if (t.value.startValue != lastMaxValue)
          t.value = { startValue: lastMaxValue, endValue: Math.max(t.value.endValue, Math.min(lastMaxValue, 100)) };
        lastMaxValue = Math.min(t.value.endValue + 0.01, 100);
      }

      // Adapt boundaries of all preceding thresholds
      let lastMinValue = Math.max(mainThreshold.value.startValue - 0.01, 0);
      for (let i2 = indexMain - 1; i2 >= 0; i2--)
      {
        let t2 = this.thresholds[i2];
        if (t2.value.endValue != lastMinValue)
          t2.value = { startValue: Math.min(t2.value.startValue, Math.max(lastMinValue, 0)), endValue: lastMinValue };
        lastMinValue = Math.max(t2.value.startValue - 0.01, 0);
      }
    }
  }
}
</script>
